/**
 * TradeRequirementsCard Component
 * Displays trade-specific requirements from jurisdiction data
 *
 * Shows:
 * - Trade-specific forms needed
 * - Key inspection checkpoints
 * - Owner-builder notice if applicable
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  FileText,
  ClipboardCheck,
  HardHat,
  AlertCircle,
  CheckCircle2,
  Info,
} from "lucide-react";
import type { Jurisdiction } from "@/hooks/useCompliance";
import type { TradeSpecificForm, InspectionCheckpoint } from "@/types/jurisdiction";

interface TradeRequirementsCardProps {
  jurisdiction: Jurisdiction;
  trade: string;
  isOwnerBuilder?: boolean;
}

/**
 * Get trade-specific forms for the given trade
 */
function getTradeSpecificForms(
  forms: Jurisdiction["tradeSpecificForms"],
  trade: string
): Record<string, TradeSpecificForm> | null {
  if (!forms) return null;

  const tradeKey = trade.toLowerCase().replace(/[^a-z]/g, "_");

  // Map common trade names to form keys
  const tradeMapping: Record<string, string> = {
    electrical: "electrical",
    plumbing: "plumbing",
    hvac: "mechanical",
    mechanical: "mechanical",
    pool: "pool",
    building: "building",
    roofing: "building",
    general: "building",
  };

  const formKey = tradeMapping[tradeKey] || tradeKey;
  return forms[formKey] || null;
}

/**
 * Get relevant inspection checkpoints for the trade
 */
function getRelevantCheckpoints(
  requirements: Jurisdiction["inspectionRequirements"],
  trade: string
): InspectionCheckpoint[] {
  if (!requirements?.standard_checkpoints) return [];

  const tradeKey = trade.toLowerCase();

  // Filter checkpoints that are relevant to this trade or are general
  return requirements.standard_checkpoints.filter((checkpoint) => {
    const name = checkpoint.name.toLowerCase();
    const desc = checkpoint.description.toLowerCase();

    // General checkpoints apply to all
    if (checkpoint.phase === "all") return true;

    // Trade-specific filtering
    if (tradeKey.includes("electrical")) {
      return (
        name.includes("electric") ||
        desc.includes("electric") ||
        checkpoint.phase === "rough-in" ||
        checkpoint.phase === "final"
      );
    }
    if (tradeKey.includes("plumb")) {
      return (
        name.includes("plumb") ||
        desc.includes("plumb") ||
        checkpoint.phase === "rough-in" ||
        checkpoint.phase === "final"
      );
    }
    if (tradeKey.includes("hvac") || tradeKey.includes("mechanical")) {
      return (
        name.includes("hvac") ||
        name.includes("mechanical") ||
        desc.includes("hvac") ||
        checkpoint.phase === "rough-in" ||
        checkpoint.phase === "final"
      );
    }
    if (tradeKey.includes("roof")) {
      return (
        name.includes("roof") ||
        desc.includes("roof") ||
        checkpoint.phase === "final"
      );
    }

    // Include all for general/building permits
    return true;
  });
}

/**
 * Get phase badge color
 */
function getPhaseBadgeVariant(
  phase: string
): "default" | "secondary" | "outline" | "destructive" {
  switch (phase.toLowerCase()) {
    case "foundation":
      return "secondary";
    case "framing":
      return "outline";
    case "rough-in":
      return "default";
    case "insulation":
      return "outline";
    case "final":
      return "destructive";
    default:
      return "secondary";
  }
}

export function TradeRequirementsCard({
  jurisdiction,
  trade,
  isOwnerBuilder = false,
}: TradeRequirementsCardProps) {
  const tradeSpecificForms = getTradeSpecificForms(
    jurisdiction.tradeSpecificForms,
    trade
  );
  const checkpoints = getRelevantCheckpoints(
    jurisdiction.inspectionRequirements,
    trade
  );
  const ownerBuilderRules = jurisdiction.ownerBuilderRules;

  // If we have no data to display, don't render the card
  if (!tradeSpecificForms && checkpoints.length === 0 && !ownerBuilderRules) {
    return null;
  }

  const formsList = tradeSpecificForms
    ? Object.entries(tradeSpecificForms)
    : [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ClipboardCheck className="h-5 w-5" />
          {trade.charAt(0).toUpperCase() + trade.slice(1)} Requirements
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Owner-Builder Alert */}
        {isOwnerBuilder && ownerBuilderRules && (
          <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800">
            <HardHat className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <AlertTitle className="text-blue-800 dark:text-blue-200">
              Owner-Builder Requirements
            </AlertTitle>
            <AlertDescription className="text-blue-700 dark:text-blue-300">
              {ownerBuilderRules.allowed ? (
                <div className="space-y-2">
                  <p className="text-sm">
                    Owner-builder permits are allowed in this jurisdiction.
                  </p>
                  {/* In-person requirement (from migration 043) */}
                  {ownerBuilderRules.in_person_required && (
                    <p className="text-sm font-medium">
                      <AlertCircle className="h-3 w-3 inline mr-1" />
                      In-person application required
                    </p>
                  )}
                  {ownerBuilderRules.requirements &&
                    ownerBuilderRules.requirements.length > 0 && (
                      <div>
                        <p className="text-sm font-medium">Requirements:</p>
                        <ul className="text-sm list-disc ml-4 space-y-1">
                          {ownerBuilderRules.requirements.map((req, idx) => (
                            <li key={idx}>{req}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  {/* Affidavit requirements (from migration 043) */}
                  {ownerBuilderRules.affidavit_requirements &&
                    ownerBuilderRules.affidavit_requirements.length > 0 && (
                      <div>
                        <p className="text-sm font-medium">Required Affidavits:</p>
                        <ul className="text-sm list-disc ml-4 space-y-1">
                          {ownerBuilderRules.affidavit_requirements.map((aff, idx) => (
                            <li key={idx}>{aff}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  {/* Subcontractor rules (from migration 043) */}
                  {ownerBuilderRules.subcontractor_rules &&
                    ownerBuilderRules.subcontractor_rules.length > 0 && (
                      <div>
                        <p className="text-sm font-medium">Subcontractor Rules:</p>
                        <ul className="text-sm list-disc ml-4 space-y-1">
                          {ownerBuilderRules.subcontractor_rules.map((rule, idx) => (
                            <li key={idx}>{rule}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  {ownerBuilderRules.roofing_special_rule &&
                    trade.toLowerCase().includes("roof") && (
                      <p className="text-sm mt-2 font-medium">
                        <AlertCircle className="h-3 w-3 inline mr-1" />
                        {ownerBuilderRules.roofing_special_rule}
                      </p>
                    )}
                </div>
              ) : (
                <p className="text-sm">
                  Owner-builder permits are not available in this jurisdiction.
                </p>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Trade-Specific Forms */}
        {formsList.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-muted-foreground" />
                <span className="font-medium">Required Forms</span>
              </div>
              <Badge variant="secondary">
                {formsList.length}
              </Badge>
            </div>
            <Accordion type="single" collapsible className="w-full">
              {formsList.map(([key, form]) => (
                <AccordionItem key={key} value={key} className="border-none">
                  <AccordionTrigger className="py-3 px-3 bg-muted/50 rounded-lg hover:bg-muted/70 mb-1">
                    <span className="flex items-center gap-2 text-sm">
                      <FileText className="h-4 w-4" />
                      {form.name}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="px-3 pt-2">
                    <div className="space-y-3 text-sm">
                      <p className="text-muted-foreground">{form.purpose}</p>
                      {form.required_fields && form.required_fields.length > 0 && (
                        <div>
                          <p className="font-medium text-xs uppercase tracking-wide text-muted-foreground mb-2">
                            Required Fields:
                          </p>
                          <ul className="list-disc ml-4 text-muted-foreground space-y-1">
                            {form.required_fields.map((field, fieldIdx) => (
                              <li key={fieldIdx}>{field}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {form.submission && (
                        <p className="text-sm text-muted-foreground">
                          <strong>Submit:</strong> {form.submission}
                        </p>
                      )}
                      {form.notes && (
                        <p className="text-sm text-muted-foreground italic">
                          {form.notes}
                        </p>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        )}

        {/* Inspection Checkpoints */}
        {checkpoints.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-muted-foreground" />
                <span className="font-medium">Inspection Checkpoints</span>
              </div>
              <Badge variant="secondary">
                {checkpoints.length}
              </Badge>
            </div>
            <div className="space-y-2">
              {checkpoints.slice(0, 6).map((checkpoint, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-3 p-3 rounded-lg bg-muted/50"
                >
                  <Badge
                    variant={getPhaseBadgeVariant(checkpoint.phase)}
                    className="shrink-0 mt-0.5"
                  >
                    {checkpoint.phase}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">{checkpoint.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {checkpoint.description}
                    </p>
                  </div>
                  {checkpoint.required && (
                    <Badge
                      variant="outline"
                      className="shrink-0"
                    >
                      Required
                    </Badge>
                  )}
                </div>
              ))}
              {checkpoints.length > 6 && (
                <p className="text-sm text-muted-foreground text-center py-2">
                  + {checkpoints.length - 6} more checkpoints
                </p>
              )}
            </div>
          </div>
        )}

        {/* No owner-builder warning for trade work */}
        {!isOwnerBuilder &&
          ownerBuilderRules &&
          trade.toLowerCase() !== "general" &&
          trade.toLowerCase() !== "building" && (
            <div className="pt-3 border-t">
              <div className="flex items-start gap-2 text-muted-foreground">
                <Info className="h-5 w-5 shrink-0 mt-0.5" />
                <p className="text-sm">
                  <strong>Note:</strong> Even for owner-builder projects, {trade}{" "}
                  work must typically be performed by a licensed contractor.
                </p>
              </div>
            </div>
          )}
      </CardContent>
    </Card>
  );
}
